import { Button, Flex, Modal, Small } from "orcs-design-system";

import { getDesignOptionLabel } from "./designOptionLabels";

type DesignOption = {
  id: string;
  name: string;
  isTrunk: boolean;
};

type ConfirmOptionActionMode = "delete" | "flatten";

type ConfirmOptionActionModalProps = {
  mode: ConfirmOptionActionMode;
  visible: boolean;
  option: DesignOption | null;
  baselineDataDate?: string | null;
  frozen?: boolean;
  onClose: () => void;
  onConfirm: (option: DesignOption) => Promise<void> | void;
};

const MODE_CONTENT: Record<
  ConfirmOptionActionMode,
  {
    headerContent: string;
    confirmLabel: string;
    confirmVariant?: "danger";
    body: (label: string) => string;
  }
> = {
  delete: {
    headerContent: "Delete option",
    confirmLabel: "Delete option",
    confirmVariant: "danger",
    body: (label) =>
      `Remove ${label}? Overlay edits for this option are discarded. The live tree is unchanged.`,
  },
  flatten: {
    headerContent: "Flatten option",
    confirmLabel: "Flatten onto live tree",
    body: (label) =>
      `Apply ${label} onto the live design tree and remove the other options? This cannot be undone from here.`,
  },
};

const ConfirmOptionActionModal = ({
  mode,
  visible,
  option,
  baselineDataDate = null,
  frozen = false,
  onClose,
  onConfirm,
}: ConfirmOptionActionModalProps) => {
  const { headerContent, confirmLabel, confirmVariant, body } =
    MODE_CONTENT[mode];

  const label = option
    ? getDesignOptionLabel(option, baselineDataDate)
    : "this option";

  return (
    <Modal
      visible={visible}
      onClose={onClose}
      headerContent={headerContent}
      footerContent={
        <Flex justifyContent="flex-end" gap="s">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={confirmVariant}
            disabled={frozen}
            onClick={async () => {
              if (!option) return;
              await onConfirm(option);
              onClose();
            }}
          >
            {confirmLabel}
          </Button>
        </Flex>
      }
    >
      <Small>{body(label)}</Small>
    </Modal>
  );
};

export default ConfirmOptionActionModal;
